import {
  classNamesFunction,
  IStyle,
  FontSizes,
  IButtonStyles,
} from '@fluentui/react';
import { color, IThemedProp } from './themesUI';

// Have to be able to accept any function type
export type StyleType<T extends (...args: Array<any>) => any> = {
  [P in keyof ReturnType<T>]: IStyle;
};

export type SubStyle<T> = Partial<NonNullable<T>> & { [key: string]: IStyle }

const commonStyles = () => {
  return {
    outlineOffset2: {
      selectors: {
        ':focus': {
          outlineOffset: '2px !important',
        },
      },
    },
    sectionHeader: {
      fontWeight: '600',
      fontSize: '14px',
      lineHeight: '20px',
    },
    textDecorationNone: {
      textDecoration: 'none !important',
    },
    fontWeight600: {
      fontWeight: '600',
    },
    fontWeightNormal: {
      fontWeight: 'normal',
    },
    fontWeightBold: {
      fontWeight: 'bold',
    },
    fontStyleItalic: {
      fontStyle: 'italic',
    },
    marginY5: {
      margin: '5px 0px',
    },
    marginY8: {
      margin: '8px 0px',
    },
    marginY10: {
      margin: '10px 0px',
    },
    marginY20: {
      margin: '20px 0px',
    },
    marginY32: {
      margin: '32px 0px',
    },
    marginTop8: {
      marginTop: '8px',
    },
    marginTop10: {
      marginTop: '10px',
    },
    marginTop24: {
      marginTop: '24px',
    },
    marginTop30: {
      marginTop: '30px',
    },
    marginTop40: {
      marginTop: '40px',
    },
    marginBottom8: {
      marginBottom: '8px',
    },
    marginBottom10: {
      marginBottom: '10px',
    },
    marginBottom20: {
      marginBottom: '20px',
    },
    marginBottom24: {
      marginBottom: '24px',
    },
    marginBottom32: {
      marginBottom: '32px',
    },
    marginBottom48: {
      marginBottom: '48px',
    },
    marginBottom45: {
      marginBottom: '45px',
    },
    marginLeft10: {
      marginLeft: '10px',
    },
    marginLeft20: {
      marginLeft: '20px',
    },
    marginLeft40: {
      marginLeft: '40px',
    },
    marginXminus20: {
      margin: '0px -20px',
    },
    flexGrow1: {
      flexGrow: 1,
    },
    textNormal: {
      fontFamily: '"Helvetica Neue",Helvetica,Arial,sans-serif',
      fontStyle: 'normal',
      fontWeight: 'normal',
      fontSize: '14px',
    },
    cursorPointer: {
      cursor: 'pointer',
    },
    contentContainer: {
      marginBottom: '16px',
    },
    title: {
      fontSize: '16px',
      fontWeight: '600',
    },
    whiteSpaceNoWrap: {
      whiteSpace: 'nowrap',
      textOverflow: 'ellipsis',
    },
    fontSize16: {
      fontSize: '16px',
    },
    fontSizeSmall: {
      fontSize: FontSizes.small,
    },
    fontSizeMedium: {
      fontSize: FontSizes.medium,
    },
    fontSizeIcon: {
      fontSize: FontSizes.icon,
    },
    fontSizeXXLarge: {
      fontSize: FontSizes.xxLarge,
    },
    fontSizeInherit: {
      fontSize: 'inherit',
    },
    maxHeight500: {
      maxHeight: '500px',
    },
    maxHeight300: {
      maxHeight: '300px',
    },
    whiteSpacePreline: {
      whiteSpace: 'pre-line',
    },
    overflowWrapBreakWord: {
      overflowWrap: 'break-word',
    },
    overflowHidden: {
      overflow: 'hidden',
    },
    overflowVisible: {
      overflow: 'visible',
    },
    overflowAuto: {
      overflow: 'auto',
    },
    flexContainerRow: {
      display: 'flex',
      flexDirection: 'row',
    },
    flexContainerColumn: {
      display: 'flex',
      flexDirection: 'column',
    },
    flexLayout: {
      display: 'flex',
    },
    flexSplit70P: {
      flexBasis: '70%',
    },
    flexSplit30P: {
      flexBasis: '30%',
    },
    flexWrap: {
      display: 'flex',
      flexWrap: 'wrap',
    },
    headerContainerRow: {
      display: 'block',
      flexDirection: 'row',
    },
    textGroupMeHeader: {
      fontFamily: "'Segoe UI', 'wf_segoe-ui_semibold', 'wf_segoe-ui_normal', 'Segoe WP', Tahoma, Arial, sans-serif !important",
    },
    ellipsisText: {
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
    resizeNone: {
      resize: 'none',
    },
    resizeBoth: {
      resize: 'both',
    },
    positionFixed: {
      position: 'fixed',
    },
    positionAbsolute: {
      position: 'absolute',
    },
    verticalAlignMiddle: {
      verticalAlign: 'middle',
    },
    hOne: {
      fontSize: '36px',
      marginTop: '20px',
      marginBottom: '10px',
      fontFamily: 'inherit',
      fontWeight: '500',
      lineHeight: '1.1',
      color: 'inherit',
    },
    inline: {
      display: 'inline',
    },
    inlineBlock: {
      display: 'inline-block',
    },
    colLgThree: {
      padding: '0',
      marginLeft: '0',
      marginRight: '0',
      float: 'left',
      position: 'relative',
      minHeight: '1px',
      alignItems: 'stretch',
      display: 'block',
      selectors: {
        '::before': {
          boxSizing: 'border-box',
        },
        ':after': {
          boxSizing: 'border-box',
        },
        '@media(min-width: 1200px)': {
          width: '25%',
        },
      },
    },
    colXsZero: {
      alignItems: 'stretch',
      float: 'left',
      width: '100%',
      margin: '0',
      position: 'relative',
      minHeight: '1px',
      padding: '0',
      marginLeft: '0',
      marginRight: '0',
    },
    colXsTwelve: {
      alignItems: 'stretch',
      float: 'left',
      width: '100%',
      margin: '0',
      position: 'relative',
      minHeight: '1px',
      padding: '0',
      marginLeft: '0',
      marginRight: '0',
    },
    colLgSeven: {
      padding: '0',
      marginLeft: '0',
      marginRight: '0',
      float: 'left',
      position: 'relative',
      minHeight: '1px',
      alignItems: 'stretch',
      margin: '0',
      selectors: {
        '::before': {
          boxSizing: 'border-box',
        },
        ':after': {
          boxSizing: 'border-box',
        },
        '@media(min-width: 1200px)': {
          width: '58.33333333%',
        },
      },
    },
    containerFluid: {
      paddingRight: '15px',
      paddingLeft: '15px',
      marginRight: 'auto',
      marginLeft: 'auto',
      selectors: {
        '::before': {
          display: 'table',
          content: "' '",
          boxSizing: 'border-box',
        },
        ':after': {
          clear: 'both',
          display: 'table',
          content: "' '",
          boxSizing: 'border-box',
        },
      },
    },
    contentAndFooterWrapper: {
      height: 'calc(100vh - 14px)',
      paddingBottom: '59px',
    },
    row: {
      padding: '0',
      margin: '0',
      selectors: {
        '::before': {
          display: 'table',
          content: "' '",
        },
        ':after': {
          clear: 'both',
          display: 'table',
          content: "' '",
        },
      },
    },
    WUSearchflag: {
      selectors:{
        '@media(min-width: 1200px)': {
          width: '85%',
        },
      },
    },
    WUContactSupportflag: {
      selectors:{
        '@media(min-width: 1200px)': {
          width: '85%',
        },
      },
    },
  };
};

interface ICommonThemedStylesProps {
  isDarkTheme?: boolean;
}

const commonThemedStyles = (props: IThemedProp & ICommonThemedStylesProps) => {
  const { theme, isDarkTheme } = props;

  return {
    textColor: {
      color: theme.palette.neutralPrimary,
    },
    SeparatorLineStyle: {
      border: '1px solid ' + '#c8c8c8',
      marginBottom: '19px',
    },
    errorFontColor: {
      color: '#FF3C00',
    },
    focusBorderStyle: {
      selectors: {
        ':focus': {
          outline: '#3B1E1E' + ' solid 1px !important',
        },
      },
    },
    blockingBackgroundColor: {
      backgroundColor: '#3B1E1E',
    },
    blockingIconColor: {
      color: '#3B1E1E',
    },
    header: {
      fontWeight: '600',
      color: isDarkTheme ? color.white : '#1B1A19',
      fontSize: '19px',
      whiteSpace: 'nowrap',
      textOverflow: 'ellipsis',
      overflow: 'hidden',
      fontFamily: "'Segoe UI',SegoeUI,'Helvetica Neue',Arial,sans-serif",
      lineHeight: '3',
    },
    h2: {
      fontWeight: '600',
      fontSize: '16px',
      lineHeight: '21px',
      marginTop: '32px',
      marginBottom: '20px',
      color: isDarkTheme ? color.white : '#1B1A19',
      fontFamily: "'Segoe UI Semibold', 'wf_segoe-ui_semibold', 'wf_segoe-ui_normal', 'Segoe UI', 'Segoe WP', Tahoma, Arial, sans-serif",
    },
    surfaceAppScrollBar: {
      selectors: {
        '::-webkit-scrollbar': {
          width: '5px',
        },
        '::-webkit-scrollbar-thumb': {
          background: color.gray,
        },
        '::-webkit-scrollbar-track': {
          background: '#transparent',
        },
      },
    },
  };
};

export const getLinkStyles = (): IButtonStyles => {
  return {
    root: {
      cursor: 'pointer',
      textDecoration: 'none !important',
    },
  };
};

// Common styles
export type ICommonStyles = StyleType<typeof commonStyles>;
export const getCommonStyles = commonStyles as () => ICommonStyles;
export const getCommonClassNames = classNamesFunction<{}, ICommonStyles>();
export const commonClassNames = getCommonClassNames(getCommonStyles);

// Common themed styles
export type ICommonThemedStyles = StyleType<typeof commonThemedStyles>;
export const getCommonThemedStyles = commonThemedStyles as (props: IThemedProp & any) => ICommonThemedStyles;
export const getCommonThemedClassNames = classNamesFunction<{}, ICommonThemedStyles>();
